/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getToken } from '../../services/api';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const authHeaders = () => {
  const token = getToken();
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

export interface BCAFormQuestion {
  id: number;
  question_text: string;
  question_type: string;
  help_text?: string;
  is_required: boolean;
  category: string;
  min_value?: number;
  max_value?: number;
  options?: Array<{
    id: number;
    option_text: string;
    option_value: string;
  }>;
}

export interface BCAForm {
  id: number;
  form_name: string;
  form_description?: string;
  frequency: string;
  estimated_completion_minutes: number;
  questions: BCAFormQuestion[];
}

export interface BCAFormStatus {
  is_filled_this_week: boolean;
  week_start: string;
  week_end: string;
  active_form?: BCAForm;
}

interface BCAFormState {
  status: BCAFormStatus | null;
  form: BCAForm | null;
  loading: boolean;
  error: string | null;
  submitting: boolean;
}

const initialState: BCAFormState = {
  status: null,
  form: null,
  loading: false,
  error: null,
  submitting: false,
};

// Async thunks
export const checkBCAFormStatus = createAsyncThunk(
  'bcaForm/checkStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE}/bca/this-week-status/`,
        authHeaders()
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch BCA form status'
      );
    }
  }
);

export const getActiveBCAForm = createAsyncThunk(
  'bcaForm/getActive',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_BASE}/bca/active-form/`,
        authHeaders()
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to fetch active BCA form'
      );
    }
  }
);

export const submitBCAForm = createAsyncThunk(
  'bcaForm/submit',
  async (
    data: {
      bca_form_id: number;
      answers: Array<{
        bca_form_question_id: number;
        answer_text?: string;
        answer_value?: string;
        answer_number?: number;
        answer_rating?: number;
        answer_json?: any;
      }>;
      scores?: Array<{
        category: string;
        score: number;
        interpretation: string;
        trend_vs_last_week?: string;
      }>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${API_BASE}/bca/submit/`,
        data,
        authHeaders()
      );
      return response.data;
    } catch (error: any) {
      // FastAPI returns 422 `detail` as an array of error objects; flatten it into
      // a readable string so the toast doesn't receive a raw object.
      const detail = error.response?.data?.detail;
      return rejectWithValue(
        Array.isArray(detail)
          ? detail.map((d: any) => `${d.loc?.slice(1).join('.')}: ${d.msg}`).join('; ')
          : detail || 'Failed to submit BCA form'
      );
    }
  }
);

const bcaFormSlice = createSlice({
  name: 'bcaForm',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkBCAFormStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(checkBCAFormStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.status = action.payload;
      state.form = action.payload.active_form;
    });
    builder.addCase(checkBCAFormStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    builder.addCase(getActiveBCAForm.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getActiveBCAForm.fulfilled, (state, action) => {
      state.loading = false;
      state.form = action.payload;
    });
    builder.addCase(getActiveBCAForm.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    builder.addCase(submitBCAForm.pending, (state) => {
      state.submitting = true;
      state.error = null;
    });
    builder.addCase(submitBCAForm.fulfilled, (state) => {
      state.submitting = false;
      state.status = { ...state.status, is_filled_this_week: true } as BCAFormStatus;
    });
    builder.addCase(submitBCAForm.rejected, (state, action) => {
      state.submitting = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError } = bcaFormSlice.actions;
export default bcaFormSlice.reducer;
